import {
    AlertTriangle,
    ArrowRight,
    CalendarClock,
    CheckCircle2,
    ChevronLeft,
    CircleDollarSign,
    LoaderCircle,
    ReceiptText,
    Repeat2,
} from 'lucide-react'
import React, { useContext, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './RescheduleAdvanceScreen.module.css'

const parseActiveStore = (rawStore) => {
    try {
        return rawStore && rawStore.trim() !== '' ? JSON.parse(rawStore) : null
    } catch {
        return null
    }
}

const formatINR = (amount) => {
    const num = parseFloat(amount)
    if (!isFinite(num)) return '—'
    return num.toLocaleString('en-IN', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    })
}

const getNextMonthCycle = () => {
    const date = new Date()
    const nextMonth = new Date(date.getFullYear(), date.getMonth() + 1, 1)
    return `${nextMonth.getFullYear()}-${String(nextMonth.getMonth() + 1).padStart(2, '0')}`
}

const normalizeCycleToISODate = (cycleMonth) => {
    if (!cycleMonth) return ''
    if (/^\d{4}-\d{2}$/.test(cycleMonth)) return `${cycleMonth}-01`
    if (/^\d{4}-\d{2}-\d{2}$/.test(cycleMonth)) return cycleMonth
    return ''
}

const formatCycleLabel = (yearMonth) => {
    if (!yearMonth) return '—'
    const [yearStr, monthStr] = yearMonth.split('-')
    const year = Number(yearStr)
    const month = Number(monthStr)
    if (!Number.isFinite(year) || !Number.isFinite(month)) return yearMonth
    const date = new Date(year, month - 1, 1)
    return date.toLocaleString('en-IN', { month: 'long', year: 'numeric' })
}

const RescheduleAdvanceScreen = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const { advanceIdentifier } = useParams()
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)
    const token = authCtx?.token

    const advance = location?.state?.advance || null
    const outstandingBalance = Number(advance?.outstandingBalance || 0)
    const currentRepaymentType = String(advance?.repaymentType || 'FIXED_EMI')

    const [formData, setFormData] = useState({
        newRepaymentType:
            currentRepaymentType === 'PERCENT_OF_NET' ? 'PERCENT_OF_NET' : 'FIXED_EMI',
        newEmiAmount: advance?.emiAmount ? String(advance.emiAmount) : '',
        newEmiPercent: advance?.emiPercent ? String(advance.emiPercent) : '',
        rescheduleFrom: getNextMonthCycle(),
        rescheduleReason: '',
    })
    const [newSchedule, setNewSchedule] = useState(null)
    const [errors, setErrors] = useState({})
    const [isSaving, setIsSaving] = useState(false)
    const [showConfirm, setShowConfirm] = useState(false)

    const projectedTenure = useMemo(() => {
        if (formData.newRepaymentType !== 'FIXED_EMI') return null
        const emi = Number(formData.newEmiAmount)
        if (!Number.isFinite(emi) || emi <= 0 || outstandingBalance <= 0) return null
        return Math.ceil(outstandingBalance / emi)
    }, [formData.newEmiAmount, formData.newRepaymentType, outstandingBalance])

    const updateField = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }))
        setErrors((prev) => ({ ...prev, [field]: undefined }))
    }

    const validateForm = () => {
        const newErrors = {}

        if (!formData.rescheduleReason.trim()) {
            newErrors.rescheduleReason = 'Provide a reason for rescheduling.'
        }

        if (formData.newRepaymentType === 'FIXED_EMI') {
            const emiAmount = Number(formData.newEmiAmount)
            if (!Number.isFinite(emiAmount) || emiAmount <= 0) {
                newErrors.newEmiAmount = 'Enter a valid EMI amount.'
            } else if (emiAmount > outstandingBalance) {
                newErrors.newEmiAmount = `Cannot exceed outstanding balance of ₹${formatINR(outstandingBalance)}.`
            }
        }

        if (formData.newRepaymentType === 'PERCENT_OF_NET') {
            const emiPercent = Number(formData.newEmiPercent)
            if (!Number.isFinite(emiPercent) || emiPercent <= 0) {
                newErrors.newEmiPercent = 'Enter a valid percentage.'
            }
        }

        if (!formData.rescheduleFrom) {
            newErrors.rescheduleFrom = 'Select a start cycle.'
        }

        return newErrors
    }

    const openConfirmModal = () => {
        const newErrors = validateForm()
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors)
            return
        }
        setErrors({})
        setShowConfirm(true)
    }

    const rescheduleAdvance = async () => {
        if (!token || !activeStore?.storeIdentifier || !advanceIdentifier) return

        const newErrors = validateForm()

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors)
            return
        }

        setShowConfirm(false)
        setErrors({})

        try {
            setIsSaving(true)

            const response = await fetch(BASE_URL_API + 'rescheduleStaffAdvance', {
                method: 'POST',
                body: JSON.stringify({
                    token,
                    storeIdentifier: activeStore.storeIdentifier,
                    advanceIdentifier,
                    rescheduledBy: authCtx?.staffIdentifier || authCtx?.authIdentifier || '',
                    newRepaymentType: formData.newRepaymentType,
                    newEmiAmount: Number(formData.newEmiAmount) || 0,
                    newEmiPercent: Number(formData.newEmiPercent) || 0,
                    rescheduleFrom: normalizeCycleToISODate(formData.rescheduleFrom),
                    rescheduleReason: formData.rescheduleReason.trim(),
                }),
            })

            const respo = await response.json()

            if (respo?.status === 'success') {
                setNewSchedule(respo.response)
                toast.success(respo?.message || 'Advance rescheduled successfully.', {
                    position: 'bottom-center',
                })
                return
            }

            if (respo?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            switch (respo?.errorCode) {
                case 'ADV_001':
                    toast.error('Advance not found.', { position: 'bottom-center' })
                    navigate(-1)
                    break
                case 'ADV_003':
                    toast.error(
                        `Cannot reschedule — advance is "${respo.response?.currentStatus || 'updated'}".`,
                        { position: 'bottom-center' }
                    )
                    navigate(-1)
                    break
                case 'ADV_006':
                    setErrors((prev) => ({ ...prev, newEmiAmount: respo?.message || 'Invalid EMI amount.' }))
                    break
                case 'VAL_001':
                    if (respo?.message?.includes('rescheduleReason')) {
                        setErrors((prev) => ({ ...prev, rescheduleReason: respo.message }))
                    } else if (respo?.message?.includes('newEmiAmount')) {
                        setErrors((prev) => ({ ...prev, newEmiAmount: respo.message }))
                    } else if (respo?.message?.includes('newEmiPercent')) {
                        setErrors((prev) => ({ ...prev, newEmiPercent: respo.message }))
                    } else {
                        toast.error(respo?.message || 'Invalid input.', { position: 'bottom-center' })
                    }
                    break
                case 'VAL_003':
                    toast.error(respo?.message || 'Invalid repayment type.', {
                        position: 'bottom-center',
                    })
                    break
                case 'VAL_005':
                    setErrors((prev) => ({ ...prev, rescheduleFrom: respo?.message || 'Invalid start cycle.' }))
                    break
                default:
                    toast.error(respo?.message || 'Reschedule failed.', {
                        position: 'bottom-center',
                    })
            }
        } catch {
            toast.error('Network error.', { position: 'bottom-center' })
        } finally {
            setIsSaving(false)
        }
    }

    if (!advanceIdentifier) {
        return (
            <div className={styles.page}>
                <div className={styles.alertCard}>
                    <AlertTriangle size={16} />
                    <div>
                        <strong>No advance selected.</strong>
                        <p>Open this screen from an active advance card.</p>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className={styles.page}>
            <div className={styles.header}>
                <button
                    className={styles.backBtn}
                    type="button"
                    onClick={() => navigate(-1)}
                    disabled={isSaving}
                >
                    <ChevronLeft size={18} />
                </button>
                <div className={styles.headerMeta}>
                    <span className={styles.heroPill}>
                        <Repeat2 size={12} />
                        Terms Update
                    </span>
                    <h1 className={styles.headerTitle}>Reschedule Advance</h1>
                    <p className={styles.headerSub}>
                        {advance?.staffName || 'Staff'} — ₹{formatINR(outstandingBalance)} outstanding
                    </p>
                </div>
            </div>

            {newSchedule ? (
                <div className={styles.resultCard}>
                    <div className={styles.resultHeader}>
                        <CheckCircle2 size={20} className={styles.resultIcon} />
                        <span>Advance Rescheduled</span>
                    </div>

                    <div className={styles.resultMetaRow}>
                        <div className={styles.metaPill}>
                            {newSchedule.previousScheduleVoided || 0} old EMIs voided
                        </div>
                        <div className={styles.metaPillPrimary}>
                            {newSchedule.newTotalInstallments || newSchedule.newSchedule?.length || 0} new EMIs created
                        </div>
                    </div>

                    <div className={styles.summaryGrid}>
                        <div className={styles.summaryCell}>
                            <span>Outstanding at Reschedule</span>
                            <strong>₹{formatINR(newSchedule.outstandingAtReschedule)}</strong>
                        </div>
                        <div className={styles.summaryCell}>
                            <span>New Repayment Type</span>
                            <strong>{newSchedule.newRepaymentType || '—'}</strong>
                        </div>
                        <div className={styles.summaryCell}>
                            <span>EMI Amount</span>
                            <strong>₹{formatINR(newSchedule.newEmiAmount)}</strong>
                        </div>
                        <div className={styles.summaryCell}>
                            <span>EMI Percent</span>
                            <strong>{Number(newSchedule.newEmiPercent || 0).toFixed(1)}%</strong>
                        </div>
                    </div>

                    <div className={styles.scheduleTableWrap}>
                        <div className={styles.scheduleHead}>
                            <span>EMI</span>
                            <span>Cycle</span>
                            <span>Amount</span>
                        </div>
                        <div className={styles.scheduleBody}>
                            {(newSchedule.newSchedule || []).map((emi) => (
                                <div className={styles.scheduleRow} key={`${emi.emiSequence}-${emi.dueCycleYear}-${emi.dueCycleMonth}`}>
                                    <span>#{emi.emiSequence}</span>
                                    <span>{emi.cycleLabel || formatCycleLabel(`${emi.dueCycleYear}-${String(emi.dueCycleMonth).padStart(2, '0')}`)}</span>
                                    <strong>₹{formatINR(emi.dueAmount)}</strong>
                                </div>
                            ))}
                        </div>
                    </div>

                    <p className={styles.resultNote}>{newSchedule.rescheduleReason || 'Repayment terms updated.'}</p>

                    <button type="button" className={styles.doneBtn} onClick={() => navigate(-1)}>
                        Done <ArrowRight size={14} />
                    </button>
                </div>
            ) : (
                <>
                    <div className={styles.summaryCard}>
                        <div className={styles.summaryHead}>
                            <ReceiptText size={14} />
                            <span>Current Terms</span>
                        </div>

                        <div className={styles.currentTermsRow}>
                            <span>Current Repayment</span>
                            <strong>
                                {currentRepaymentType === 'PERCENT_OF_NET'
                                    ? `${Number(advance?.emiPercent || 0).toFixed(1)}% of Net`
                                    : `₹${formatINR(advance?.emiAmount || 0)}/month`}
                            </strong>
                        </div>

                        <div className={styles.currentTermsRow}>
                            <span>Paid So Far</span>
                            <strong>
                                ₹{formatINR(advance?.totalRepaid || 0)} of ₹{formatINR(advance?.disbursedAmount || advance?.approvedAmount || 0)}
                            </strong>
                        </div>
                    </div>

                    <div className={styles.formCard}>
                        <div className={styles.toggleWrap}>
                            <button
                                type="button"
                                className={
                                    formData.newRepaymentType === 'FIXED_EMI'
                                        ? `${styles.toggleBtn} ${styles.toggleBtnActive}`
                                        : styles.toggleBtn
                                }
                                onClick={() => updateField('newRepaymentType', 'FIXED_EMI')}
                                disabled={isSaving}
                            >
                                Fixed EMI
                            </button>
                            <button
                                type="button"
                                className={
                                    formData.newRepaymentType === 'PERCENT_OF_NET'
                                        ? `${styles.toggleBtn} ${styles.toggleBtnActive}`
                                        : styles.toggleBtn
                                }
                                onClick={() => updateField('newRepaymentType', 'PERCENT_OF_NET')}
                                disabled={isSaving}
                            >
                                % of Net
                            </button>
                        </div>

                        {formData.newRepaymentType === 'FIXED_EMI' ? (
                            <div className={styles.fieldGroup}>
                                <label className={styles.fieldLabel}>
                                    <CircleDollarSign size={13} /> New Monthly EMI (₹) *
                                </label>
                                <input
                                    type="number"
                                    className={`${styles.textInput} ${errors?.newEmiAmount ? styles.inputError : ''}`}
                                    value={formData.newEmiAmount}
                                    onChange={(event) => updateField('newEmiAmount', event.target.value)}
                                    min="0"
                                    disabled={isSaving}
                                />
                                <span className={styles.fieldHint}>
                                    New tenure:{' '}
                                    {projectedTenure ? `${projectedTenure} month(s)` : '—'}
                                </span>
                                {errors?.newEmiAmount ? (
                                    <span className={styles.errorText}>{errors.newEmiAmount}</span>
                                ) : null}
                            </div>
                        ) : (
                            <div className={styles.fieldGroup}>
                                <label className={styles.fieldLabel}>
                                    <CircleDollarSign size={13} /> Deduction % per Month *
                                </label>
                                <input
                                    type="number"
                                    className={`${styles.textInput} ${errors?.newEmiPercent ? styles.inputError : ''}`}
                                    value={formData.newEmiPercent}
                                    onChange={(event) => updateField('newEmiPercent', event.target.value)}
                                    min="0"
                                    step="0.1"
                                    disabled={isSaving}
                                />
                                {errors?.newEmiPercent ? (
                                    <span className={styles.errorText}>{errors.newEmiPercent}</span>
                                ) : null}
                            </div>
                        )}

                        <div className={styles.fieldGroup}>
                            <label className={styles.fieldLabel}>
                                <CalendarClock size={13} /> Reschedule From *
                            </label>
                            <input
                                type="month"
                                className={`${styles.textInput} ${errors?.rescheduleFrom ? styles.inputError : ''}`}
                                value={formData.rescheduleFrom}
                                onChange={(event) => updateField('rescheduleFrom', event.target.value)}
                                disabled={isSaving}
                            />
                            {errors?.rescheduleFrom ? (
                                <span className={styles.errorText}>{errors.rescheduleFrom}</span>
                            ) : null}
                        </div>

                        <div className={styles.fieldGroup}>
                            <label className={styles.fieldLabel}>Reason for Reschedule *</label>
                            <textarea
                                className={`${styles.textArea} ${errors?.rescheduleReason ? styles.inputError : ''}`}
                                rows={4}
                                value={formData.rescheduleReason}
                                onChange={(event) => updateField('rescheduleReason', event.target.value)}
                                placeholder="Document why repayment terms are being changed."
                                disabled={isSaving}
                            />
                            {errors?.rescheduleReason ? (
                                <span className={styles.errorText}>{errors.rescheduleReason}</span>
                            ) : null}
                        </div>

                        <button
                            type="button"
                            className={styles.rescheduleBtn}
                            onClick={openConfirmModal}
                            disabled={isSaving}
                        >
                            {isSaving ? (
                                <>
                                    <LoaderCircle size={15} className={styles.spinIcon} />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    Reschedule <ArrowRight size={14} />
                                </>
                            )}
                        </button>
                    </div>
                </>
            )}

            {showConfirm ? (
                <div className={styles.overlay}>
                    <div className={styles.confirmDialog}>
                        <div className={styles.confirmIcon}>
                            <AlertTriangle size={22} />
                        </div>
                        <h2 className={styles.confirmTitle}>Confirm Reschedule?</h2>
                        <p className={styles.confirmBody}>
                            {formData.newRepaymentType === 'FIXED_EMI' ? (
                                <>
                                    This will apply a new EMI of <strong>₹{formatINR(formData.newEmiAmount)}</strong> from{' '}
                                    <strong>{formatCycleLabel(formData.rescheduleFrom)}</strong>.
                                </>
                            ) : (
                                <>
                                    This will switch repayment to <strong>{Number(formData.newEmiPercent || 0).toFixed(1)}% of net</strong>{' '}
                                    from <strong>{formatCycleLabel(formData.rescheduleFrom)}</strong>.
                                </>
                            )}
                        </p>
                        <div className={styles.confirmActions}>
                            <button
                                type="button"
                                className={styles.cancelBtn}
                                onClick={() => setShowConfirm(false)}
                                disabled={isSaving}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className={styles.confirmBtn}
                                onClick={rescheduleAdvance}
                                disabled={isSaving}
                            >
                                {isSaving ? (
                                    <>
                                        <LoaderCircle size={15} className={styles.spinIcon} />
                                        Saving...
                                    </>
                                ) : (
                                    'Yes, Reschedule'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    )
}

export default RescheduleAdvanceScreen